import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box } from '@mui/material';
import InlineAlertBanner from '@sharedCR/common/InlineAlertBanner';
import FileUploadCDS from '@sharedCR/coreDesignSystem/FileUploadCDS';
import { DateFieldCDS } from '@sharedCR/coreDesignSystem/form';
import ModalFormWrapper from 'app/shared/componentsRestyled/coreDesignSystem/ModalFormWrapper';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { StatusEnum } from 'constants/status';
import useForm from 'hooks/useForm';

import { getFormBundleAction } from 'store/forms/actions';
import { closeDialogAction } from 'store/global/actions';

import {
  UPLOAD_FORM_DELIVERY_ACCEPTED_FILE_TYPES,
  UPLOAD_FORM_DELIVERY_BUNDLE_UNAVAILABLE_MESSAGE,
  UPLOAD_FORM_DELIVERY_FILE_REQUIRED_MESSAGE,
  UploadFormDeliveryBundleStatus,
} from './UploadFormDeliveryForm.constants';
import { UploadFormDeliveryFormTestIds } from './UploadFormDeliveryForm.testIds';
import {
  getBundleDocumentTypeId,
  getUploadFormDeliverySchema,
} from './UploadFormDeliveryForm.utils';

const UploadFormDeliveryForm = ({ formBundleId = null, isLoading, setIsLoading, onSubmit }) => {
  const dispatch = useDispatch();

  const {
    form,
    setSchema,
    setInForm,
    isFormChanged,
    validateForm,
    validateField,
    validationErrors,
  } = useForm({});

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileErrorMessage, setFileErrorMessage] = useState(null);
  const [bundleStatus, setBundleStatus] = useState(UploadFormDeliveryBundleStatus.LOADING);
  const [documentTypeId, setDocumentTypeId] = useState(null);

  const isBundleUnavailable = bundleStatus === UploadFormDeliveryBundleStatus.UNAVAILABLE;
  const isBundleReady = bundleStatus === UploadFormDeliveryBundleStatus.READY;

  const onDateFieldChange = useCallback(
    (value, name) => {
      setInForm(name, value);
      validateField(name, value);
    },
    [setInForm, validateField],
  );

  const onClose = useCallback(() => {
    dispatch(closeDialogAction());
  }, [dispatch]);

  const onSubmitForm = useCallback(async () => {
    if (!selectedFile) {
      setFileErrorMessage(UPLOAD_FORM_DELIVERY_FILE_REQUIRED_MESSAGE);

      return;
    }

    if (!(await validateForm())) {
      return;
    }

    try {
      setIsLoading(true);

      await onSubmit({
        file: selectedFile,
        sentDate: form.sentDate,
        eSignedDate: form.eSignedDate,
        documentTypeId,
      });
      dispatch(closeDialogAction());
    } finally {
      setIsLoading(false);
    }
  }, [validateForm, form, onSubmit, selectedFile, documentTypeId, setIsLoading, dispatch]);

  useEffect(() => {
    setSchema(getUploadFormDeliverySchema());
  }, [setSchema]);

  useEffect(() => {
    const request = { isActive: true };

    const bundleDocumentTypeIdPromise = _.isNil(formBundleId)
      ? Promise.resolve(null)
      : dispatch(getFormBundleAction({ formBundleId, silent: true })).then(
          getBundleDocumentTypeId,
          () => null,
        );

    bundleDocumentTypeIdPromise.then(bundleDocumentTypeId => {
      if (!request.isActive) {
        return;
      }

      setDocumentTypeId(bundleDocumentTypeId);
      setBundleStatus(
        _.isNil(bundleDocumentTypeId)
          ? UploadFormDeliveryBundleStatus.UNAVAILABLE
          : UploadFormDeliveryBundleStatus.READY,
      );
    });

    return () => {
      request.isActive = false;
    };
  }, [dispatch, formBundleId]);

  return (
    <ModalFormWrapper
      fetchData={_.noop}
      isError={false}
      isFormChanged={isFormChanged || !!selectedFile}
      isSubmitButtonDisabled={isLoading || !isBundleReady}
      isSubmitting={isLoading}
      submitButtonName="Submit"
      submitButtonTestId={UploadFormDeliveryFormTestIds.SUBMIT_BUTTON}
      onClose={onClose}
      onSubmit={onSubmitForm}
    >
      {isBundleUnavailable && (
        <InlineAlertBanner
          dataTestId={UploadFormDeliveryFormTestIds.BUNDLE_UNAVAILABLE_ALERT}
          message={UPLOAD_FORM_DELIVERY_BUNDLE_UNAVAILABLE_MESSAGE}
          variant={StatusEnum.ERROR}
        />
      )}
      <FileUploadCDS
        acceptedFileTypes={UPLOAD_FORM_DELIVERY_ACCEPTED_FILE_TYPES}
        callToActionMessage="Upload the signed form delivery PDF"
        dataTestId={UploadFormDeliveryFormTestIds.FILE_UPLOAD}
        disabled={isLoading}
        errorMessage={fileErrorMessage}
        selectedFile={selectedFile}
        setErrorMessage={setFileErrorMessage}
        setSelectedFile={setSelectedFile}
      />
      <Box className="flex gap-12">
        <DateFieldCDS
          required
          dataTestId={UploadFormDeliveryFormTestIds.SENT_DATE}
          disabled={isLoading}
          error={!!validationErrors.sentDate}
          helperText={validationErrors.sentDate}
          label="Sent Date"
          name="sentDate"
          value={form.sentDate || null}
          onChange={onDateFieldChange}
        />
        <DateFieldCDS
          dataTestId={UploadFormDeliveryFormTestIds.E_SIGNED_DATE}
          disabled={isLoading}
          error={!!validationErrors.eSignedDate}
          helperText={validationErrors.eSignedDate}
          label="E-Signed Date"
          name="eSignedDate"
          value={form.eSignedDate || null}
          onChange={onDateFieldChange}
        />
      </Box>
    </ModalFormWrapper>
  );
};

UploadFormDeliveryForm.propTypes = {
  ...WithLoadingPropTypes,
  onSubmit: PropTypes.func.isRequired,
  formBundleId: PropTypes.string,
};

export default compose(withLoading, memo)(UploadFormDeliveryForm);
